import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import Hero from './components/Hero';
import Shop from './components/Shop';
import About from './components/About';
import Signup from './components/Signup';
import Signin from './components/Signin';
import Account from './components/Account';
import AdminDashboard from './components/AdminDashboard';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import Footer from './components/Footer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useCart } from './hooks/useCart';

function AppContent() {
  const { loading: authLoading } = useAuth();
  const { cartItems, addToCart, removeFromCart, updateCartQuantity, getCartItemCount, clearCart: clearCartFromHook } = useCart();
  const [cartItemList, setCartItemList] = useState<typeof cartItems>([]);
  const [currentPage, setCurrentPage] = useState<'home' | 'archive' | 'new-arrivals' | 'band-tees' | 'about' | 'signup' | 'signin' | 'account' | 'admin' | 'checkout'>('home');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [appReady, setAppReady] = useState(false);

  // Sync cartItems from useCart to local state
  useEffect(() => {
    setCartItemList(cartItems);
  }, [cartItems]);

  // Clear cart function - uses hook's clearCart
  const clearCart = () => {
    clearCartFromHook();
    setCartItemList([]);
  };

  // Safety timeout to ensure app always loads
  useEffect(() => {
    const timeout = setTimeout(() => {
      console.warn('App: Safety timeout reached, forcing app ready');
      setAppReady(true);
    }, 15000); // 15 seconds max

    return () => clearTimeout(timeout);
  }, []);

  // When auth is done loading, mark app as ready
  useEffect(() => {
    if (!authLoading) {
      setAppReady(true);
    }
  }, [authLoading]);

  // Check for success parameter and show checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setCurrentPage('checkout');
    }
  }, []);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const toggleCart = () => {
    setIsCartOpen(prev => !prev);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'home':
        return <Hero onShopNow={() => setCurrentPage('archive')} />;
      case 'archive':
        return (
          <Shop
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            onAddToCart={addToCart}
            category="all"
          />
        );
      case 'new-arrivals':
        return (
          <Shop
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            onAddToCart={addToCart}
            category="all"
            newArrivals={true}
          />
        );
      case 'band-tees':
        return (
          <Shop
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            onAddToCart={addToCart}
            category="teeshirts"
          />
        );
      case 'about':
        return <About />;
      case 'signup':
        return <Signup onNavigate={setCurrentPage} />;
      case 'signin':
        return <Signin onNavigate={setCurrentPage} />;
      case 'account':
        return <Account />;
      case 'admin':
        return <AdminDashboard />;
      case 'checkout':
        return (
          <Checkout
            cartItems={cartItems}
            onBackToCart={() => {
              setCurrentPage('home');
              setIsCartOpen(true);
            }}
            onClearCart={clearCart}
            onContinueShopping={() => {
              setCurrentPage('archive');
              setIsCartOpen(false);
            }}
          />
        );
      default:
        return <Hero onShopNow={() => setCurrentPage('archive')} />;
    }
  };

  // Show loading only if app is not ready AND we're still in initial load
  if (!appReady && authLoading) {
    return (
      <div className="App">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Header
        onNavigate={setCurrentPage}
        currentPage={currentPage}
        onSearch={handleSearch}
        searchTerm={searchTerm}
        onToggleCart={toggleCart}
        cartItemCount={getCartItemCount()}
      />
      {renderCurrentPage()}
      <Footer />
      {isCartOpen && (
        <Cart
          cartItems={cartItems}
          onRemoveItem={removeFromCart}
          onUpdateQuantity={updateCartQuantity}
          onClose={closeCart}
          onCheckout={() => setCurrentPage('checkout')}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
